import type { QueryClient } from '@tanstack/react-query';
import { esDenegacion, notificarLecturaDenegada } from './sesionPerdida';

/**
 * Respaldo global para las LECTURAS.
 *
 * El `QueryClient` ya trae un `MutationCache.onError` que saca un aviso cuando
 * una escritura falla sin que nadie la atienda. Del lado de la lectura no había
 * nada: más de quince `useQuery` podían fallar —RLS, JWT caducado, 500— y
 * quedarse en el `isError` de un componente que a lo mejor ni lo pinta. Ningún
 * ojo, ninguna fila en el registro.
 *
 * Esto engancha un `onError` en el `QueryCache` que deja constancia por el
 * camino de siempre: `console.error` con una CADENA SUELTA, que
 * `utils/consolePatch.ts` convierte en `reportFailure`. Nunca el objeto de
 * error: un `PostgrestError` serializado trae `"code":"42501"` o
 * `"code":"PGRST303"` dentro, y esos dos patrones son ruido para el filtro del
 * logger — pasar el objeto sería reportar a un sitio donde el informe se tira.
 *
 * Se respeta el mismo `meta: { suppressGlobalError: true }` que ya usan las
 * mutaciones (ver `hooks/useTimeClockQueries.ts`) para el caso de «esta la
 * atiendo yo y enseño mi propio mensaje».
 *
 * ── OJO AL VOLUMEN, y con detalle ──────────────────────────────────────────
 *
 * La cadena suelta no es sólo por gusto: al no serializar el `PostgrestError`,
 * el mensaje deja de encajar con DOS filtros de ruido que `utils/logger.ts`
 * tiene puestos a propósito (`/"code":\s*"42501"/` en :135 y
 * `/"code"\s*:\s*"PGRST303"/` en :143). O sea que esto no saca a la luz sólo
 * «fallos que antes eran silenciosos»: saca también fallos que el registro
 * descartaba a conciencia por no accionables. Concretamente, a partir de ahora
 * el panel verá:
 *
 *   - `42501` — RLS negando el acceso, que muchas veces es la política
 *     haciendo su trabajo. Estas son las que más van a crecer: son
 *     DETERMINISTAS, así que los `retry: 2` del QueryClient no las amortiguan
 *     (fallan los tres intentos) y llega una por lectura y por montaje.
 *   - `PGRST303` — JWT caducado. Se cura solo (gotrue renueva y React Query
 *     refetch al volver el foco), pero deja su línea.
 *
 * Lo que NO cambia: el ruido de candados de gotrue (`Lock broken by another
 * request…`) lo filtra un patrón de TEXTO, no de JSON, así que sigue sin
 * llegar aunque venga por aquí.
 *
 * En sitios: 17 `useQuery` en el árbol, con `retry: 2` delante. Un fallo
 * transitorio (red) necesita tres tropiezos seguidos para asomar; uno
 * determinista (RLS, tabla mal permisada) asoma siempre.
 *
 * Si el integrador decide que 42501 y PGRST303 deben seguir callados, el sitio
 * para decidirlo es AQUÍ —filtrando por `(error as any)?.code` antes del
 * `console.error`— y no en `logger.ts`, que ya no los ve venir. Conviene
 * decidirlo antes de fusionar, no el primer día.
 *
 * ── Y una denegación se AVISA, además de anotarse ──────────────────────────
 *
 * Un «permission denied» con la sesión aparentemente viva puede ser la sesión
 * muerta por debajo: gotrue sin nada guardado y supabase-js mandando la llave
 * publicable en cada petición. Aquí no se sabe; AuthContext sí (mira si queda
 * token guardado). Se le avisa por `services/sesionPerdida` —un puente sin
 * imports— y él decide. Ver el encabezado de ese archivo.
 *
 * @returns `true` si lo instaló, `false` si ya había un `onError` puesto. No
 *   pisa al que estuviera: si `services/queryClient.ts` acaba trayendo el suyo
 *   en el constructor, este se aparta y no hay informes por duplicado.
 */
export function vigilarLecturas(cliente: QueryClient): boolean {
    const cache = cliente.getQueryCache();
    if (cache.config.onError) return false;

    cache.config.onError = (error, query) => {
        if (query.meta?.suppressGlobalError) return;
        const clave = String(query.queryKey?.[0] ?? 'sin-clave');
        const motivo = (error as Error | null)?.message || String(error);
        console.error(`[lectura:${clave}] ${motivo}`);
        if (esDenegacion(error)) notificarLecturaDenegada(`lectura:${clave}`, motivo);
    };

    return true;
}
